import { useEffect, useState } from "react";
import { api, unwrap } from "@/lib/api";
import { Tag, Plus, X, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { COUPONS } from "@/constants/testIds";

export function CouponsManager({ branches = [] }) {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = () => {
    api.get("/coupons").then((r) => setItems(unwrap(r) || [])).catch(() => {});
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  const deactivate = async (id) => {
    try {
      await api.delete(`/coupons/${id}`);
      toast.success("Coupon deactivated");
      refresh();
    } catch {
      toast.error("Could not deactivate coupon");
    }
  };

  return (
    <div className="glass-primary rounded-[20px] overflow-hidden" data-testid={COUPONS.root}>
      <button
        onClick={() => setOpen((x) => !x)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/30 transition"
        data-testid={COUPONS.toggle}
      >
        <div className="flex items-center gap-3">
          <Tag size={16} className="text-[var(--color-purple)]" />
          <div>
            <div className="font-sora font-semibold text-[15px] text-[var(--color-text-primary)]">Coupons</div>
            <div className="font-inter text-[12px] text-[var(--color-text-muted)]">
              Promotional codes redeemable at billing
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {items.length > 0 && (
            <span className="font-mono text-[12px] text-[var(--color-text-muted)]">{items.filter((c) => c.is_active).length} active</span>
          )}
          {open ? <ChevronUp size={16} className="text-[var(--color-text-muted)]" /> : <ChevronDown size={16} className="text-[var(--color-text-muted)]" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 anim-slide-up space-y-3">
          <div className="flex justify-end">
            <button
              className="btn-3d btn-gold-3d btn-sm"
              onClick={() => setCreateOpen(true)}
              data-testid={COUPONS.createBtn}
            >
              <Plus size={14} /> Create coupon
            </button>
          </div>

          {items.length === 0 ? (
            <div className="font-inter text-[13px] text-[var(--color-text-muted)] text-center py-6">
              No coupons yet. Create one to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="font-inter text-[11px] uppercase tracking-[0.1em] text-[var(--color-text-muted)]">
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Value</th>
                    <th className="py-2 pr-3">Valid until</th>
                    <th className="py-2 pr-3">Used / Limit</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} className="border-t border-white/40" data-testid={COUPONS.row(c.id)}>
                      <td className="py-2.5 pr-3 font-mono text-[12.5px] text-[var(--color-mauve-deep)]">{c.code}</td>
                      <td className="py-2.5 pr-3 font-inter text-[12px] text-[var(--color-text-secondary)]">{c.type}</td>
                      <td className="py-2.5 pr-3 font-mono text-[12.5px]">{c.type === "percentage" ? `${c.value}%` : `₹${c.value}`}</td>
                      <td className="py-2.5 pr-3 font-mono text-[12px] text-[var(--color-text-secondary)]">{c.valid_until}</td>
                      <td className="py-2.5 pr-3 font-mono text-[12px]">{c.used_count}{c.usage_limit !== null && c.usage_limit !== undefined ? ` / ${c.usage_limit}` : " / ∞"}</td>
                      <td className="py-2.5 pr-3">
                        <span className="status-pill" style={c.is_active ? { background: "rgba(22,163,74,0.10)", color: "#15803D" } : { background: "rgba(120,113,108,0.10)", color: "#78716C" }}>
                          {c.is_active ? "ACTIVE" : "OFF"}
                        </span>
                      </td>
                      <td className="py-2.5">
                        {c.is_active && (
                          <button
                            onClick={() => deactivate(c.id)}
                            className="w-7 h-7 rounded-full glass-subtle flex items-center justify-center hover:bg-white/80"
                            data-testid={COUPONS.deactivateBtn(c.id)}
                            aria-label="Deactivate coupon"
                          >
                            <Trash2 size={12} className="text-[var(--color-danger-deep)]" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {createOpen && <CreateCouponModal branches={branches} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); refresh(); }} />}
    </div>
  );
}

function CreateCouponModal({ branches, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthLater = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    code: "",
    type: "percentage",
    value: "",
    min_bill_amount: "",
    max_discount_amount: "",
    valid_from: today,
    valid_until: monthLater,
    usage_limit: "",
    unlimited: true,
    branch_id: "",
  });

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const submit = async () => {
    if (!form.code.trim()) { toast.error("Coupon code is required"); return; }
    const value = Number(form.value);
    if (!value || value <= 0) { toast.error("Value must be greater than zero"); return; }
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value,
        min_bill_amount: form.min_bill_amount ? Number(form.min_bill_amount) : null,
        max_discount_amount: form.type === "percentage" && form.max_discount_amount ? Number(form.max_discount_amount) : null,
        valid_from: form.valid_from,
        valid_until: form.valid_until,
        usage_limit: form.unlimited ? null : (form.usage_limit ? Number(form.usage_limit) : null),
        applicable_services: [],
        branch_id: form.branch_id || null,
        is_active: true,
      };
      await api.post("/coupons", payload);
      toast.success(`Coupon ${payload.code} created`);
      onCreated?.();
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Couldn't create coupon", { description: data?.code || "" });
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center anim-fade-in"
      style={{ background: "rgba(28,25,23,0.32)", backdropFilter: "blur(12px)" }}
      data-testid={COUPONS.createModal}
    >
      <div className="glass-elevated rounded-[24px] p-6 w-[min(560px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg space-y-3">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="overline mb-1">New coupon</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">Create promotional code</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        <Row label="Code">
          <input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="WELCOME20"
            className={`${inputCls} uppercase font-mono`}
            data-testid={COUPONS.codeInput}
          />
        </Row>

        <div>
          <div className="overline mb-1.5">Type</div>
          <div className="flex gap-1.5">
            {["percentage", "flat"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setForm({ ...form, type: t })}
                className={`px-3 py-1.5 rounded-full text-[12.5px] font-inter font-medium border transition ${form.type === t ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}
                data-testid={COUPONS.typeToggle(t)}
              >
                {t === "percentage" ? "% Percentage" : "₹ Flat amount"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Row label={form.type === "percentage" ? "Discount %" : "Flat ₹ off"}>
            <input
              type="number" step="1" min="1"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              className={inputCls}
              data-testid={COUPONS.valueInput}
            />
          </Row>
          <Row label="Minimum bill ₹ (optional)">
            <input
              type="number" step="1" min="0"
              value={form.min_bill_amount}
              onChange={(e) => setForm({ ...form, min_bill_amount: e.target.value })}
              className={inputCls}
              data-testid={COUPONS.minBillInput}
            />
          </Row>
        </div>

        {form.type === "percentage" && (
          <Row label="Cap on discount ₹ (optional)">
            <input
              type="number" step="1" min="0"
              value={form.max_discount_amount}
              onChange={(e) => setForm({ ...form, max_discount_amount: e.target.value })}
              className={inputCls}
              data-testid={COUPONS.maxDiscountInput}
            />
          </Row>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Row label="Valid from">
            <input
              type="date"
              value={form.valid_from}
              onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
              className={inputCls}
              data-testid={COUPONS.validFromInput}
            />
          </Row>
          <Row label="Valid until">
            <input
              type="date"
              value={form.valid_until}
              onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              className={inputCls}
              data-testid={COUPONS.validUntilInput}
            />
          </Row>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Row label="Usage limit">
            <div className="flex items-center gap-2">
              <input
                type="number" step="1" min="1"
                disabled={form.unlimited}
                value={form.usage_limit}
                onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                className={`${inputCls} ${form.unlimited ? "opacity-50" : ""}`}
                data-testid={COUPONS.usageLimitInput}
                placeholder="100"
              />
              <button
                type="button"
                onClick={() => setForm({ ...form, unlimited: !form.unlimited })}
                className={`px-3 py-2 rounded-full text-[11.5px] font-inter font-medium border whitespace-nowrap ${form.unlimited ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}
                data-testid={COUPONS.unlimitedToggle}
              >
                Unlimited
              </button>
            </div>
          </Row>
          <Row label="Branch">
            <select
              value={form.branch_id}
              onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
              className={inputCls}
              data-testid={COUPONS.branchSelect}
            >
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </Row>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-3d btn-glass-3d btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-3d btn-gold-3d" onClick={submit} data-testid={COUPONS.createSubmitBtn}>
            <Plus size={14} /> Create
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[14px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-gold-mid)] focus:ring-2 focus:ring-[var(--color-gold-mid)]/20 transition";

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
